import * as stylex from '@stylexjs/stylex';
import { Focusable, TooltipTrigger } from 'react-aria-components';

import { theme } from '@/constants/theme';
import { Badge, tableOptions, Text, Tooltip } from '@/libs/serenity-ui';
import type { campaignEditRequestQueries } from '@/resources/campaign-edit-request/campaign-edit-request.api';
import type { QueryResultData } from '@/types/query-result-data.type';

type EditRequestDataRow = QueryResultData<typeof campaignEditRequestQueries.list>['data'][number];

export type EditRequestParentRow = EditRequestDataRow & {
  __rowKind: 'parent';
  isExpanded: boolean;
};

export interface EditRequestSubRow {
  __rowKind: 'sub';
  parentRowId: string;
  itemKey: string;
  description: string;
}

export type EditRequestFlatRow = EditRequestParentRow | EditRequestSubRow;

export function isSubRow (row: EditRequestFlatRow): row is EditRequestSubRow {
  // eslint-disable-next-line no-underscore-dangle
  return row.__rowKind === 'sub';
}

export const campaignEditRequestTableOptions = tableOptions<EditRequestFlatRow>([
  {
    id: 'status',
    label: 'Status',
    children: (label) => <Text>{label}</Text>,
    getter: (row) => (isSubRow(row) ? null : row.transformed.forLabels.status),
    minWidth: 120,
    allowsResizing: true,
    allowsSorting: false,
    cell: (row) => {
      if (isSubRow(row)) {
        return { children: null };
      }
      return {
        textValue: row.transformed.forLabels.status,
        children: <Badge {...theme.badge[row.transformed.forLabels.status]}>{row.transformed.forLabels.status}</Badge>,
      };
    },
  },
  {
    id: 'user',
    label: 'Requested by',
    children: (label) => <Text>{label}</Text>,
    getter: (row) => (isSubRow(row) ? null : row.meta.createdByFullName),
    minWidth: 57,
    allowsResizing: true,
    allowsSorting: false,
    cell: (row) => {
      if (isSubRow(row)) {
        return { children: null };
      }
      return {
        textValue: row.meta.createdByFullName ?? undefined,
        children: <Text>{row.meta.createdByFullName}</Text>,
      };
    },
  },
  {
    id: 'action',
    label: 'Change requested',
    children: (label) => <Text>{label}</Text>,
    getter: (row) => (isSubRow(row) ? row.description : row.meta.summaryLabel),
    minWidth: 156,
    allowsResizing: true,
    allowsSorting: false,
    cell: (row) => {
      if (isSubRow(row)) {
        return {
          textValue: row.description,
          children: <Text style={subRowStyles.indent}>{row.description}</Text>,
        };
      }

      const reasonTooltip = row.meta.reasonTooltip;
      const label = <Text>{row.meta.summaryLabel}</Text>;

      if (!reasonTooltip) {
        return {
          textValue: row.meta.summaryLabel,
          children: label,
        };
      }

      return {
        children: (
          <TooltipTrigger delay={0}>
            <Focusable>
              <Text style={reasonTooltipStyles.trigger}>{row.meta.summaryLabel}</Text>
            </Focusable>
            <Tooltip placement="bottom" style={reasonTooltipStyles.tooltip}>

              {reasonTooltip.declineReason != null && (
                <Text style={reasonTooltipStyles.reason}>{`Decline reason: ${reasonTooltip.declineReason}`}</Text>
              )}
              {reasonTooltip.pauseReason != null && (
                <Text style={reasonTooltip.declineReason != null ? reasonTooltipStyles.reasonWithSpacing : reasonTooltipStyles.reason}>
                  {`Pause reason: ${reasonTooltip.pauseReason}`}
                </Text>
              )}
            </Tooltip>
          </TooltipTrigger>
        ),
      };
    },
  },
  {
    id: 'createdAt',
    label: 'Requested',
    children: (label) => <Text>{label}</Text>,
    getter: (row) => (isSubRow(row) ? null : row.transformed.forLabels.createdAt),
    minWidth: 97,
    allowsResizing: true,
    allowsSorting: false,
    cell: (row) => {
      if (isSubRow(row)) {
        return { children: null };
      }
      return {
        align: 'right',
        textValue: row.transformed.forLabels.createdAt,
        children: <Text>{row.transformed.forLabels.createdAt}</Text>,
      };
    },
  },
]);

export const subRowStyles = stylex.create({
  indent: {
    paddingLeft: 16,
  },
});

export const expandChevronStyles = stylex.create({
  wrapper: {
    alignItems: 'center',
    display: 'flex',
    gap: 4,
    minWidth: 0,
    overflow: 'hidden',
    width: '100%',
  },
  icon: {
    flexShrink: 0,
    transition: 'transform 0.15s ease',
  },
  iconExpanded: {
    transform: 'rotate(90deg)',
  },
});

const reasonTooltipStyles = stylex.create({
  reason: {
    display: 'block',
    maxWidth: '100%',
    minWidth: 0,
    overflowWrap: 'anywhere',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  reasonWithSpacing: {
    display: 'block',
    marginTop: 8,
    maxWidth: '100%',
    minWidth: 0,
    overflowWrap: 'anywhere',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  tooltip: {
    alignItems: 'flex-start',
    flexDirection: 'column',
    gap: 0,
    maxWidth: 300,
    minWidth: 0,
  },
  trigger: {
    cursor: 'default',
    display: 'inline-flex',
    width: '100%',
  },
});
